import Vue from 'vue';
import Vuex from 'vuex';
import menuApi from '@/api/menu-api';
import countApi from '@/api/count-api';
import common from '@/js/common';
import hrApi from '@/api/hr-api';
import erpApi from '@/api/erp-api';
import assetApi from '@/api/asset-api';

Vue.use(Vuex);

const initialState = () => {
  return {
    menuInfoList: [],
    platformMenuInfoList: [],
    // platformMenuAccess : { hr: boolean, bill: boolean, accounting: boolean }
    platformMenuAccess: null,
    domain: '',
    countInfo: {},
  };
};

const getters = {
  getMenuRecentCount: (state) => (menuType) => {
    if (menuType === 'BOARD_LINK') {
      return state.countInfo.board;
    }
    if (menuType === 'MAIL_LINK') {
      return state.countInfo.mail;
    }
    if (menuType === 'EA_LINK') {
      return state.countInfo.approval;
    }
    if (menuType === 'SHARED_LINK') {
      return state.countInfo.shared;
    }
    if (menuType === 'NOTES_LINK') {
      return state.countInfo.memo;
    }
    return null;
  },
};

const mutations = {
  setMenuInfoList(state, menuInfoList) {
    state.menuInfoList = menuInfoList;
  },
  setPlatformMenuInfoList(state, menuInfoList) {
    state.platformMenuInfoList = menuInfoList;
  },
  setDomain(state, domain) {
    state.domain = domain;
  },
  setMenuRecentCount(state, menuRecentCountInfo) {
    state.countInfo = menuRecentCountInfo;
    let menuInfoList = [...state.menuInfoList];
    menuInfoList.forEach((menuInfo, index) => {
      state.menuInfoList[index]['recent_count'] = 0;
      if (menuInfo['menu_type'] === 'MAIL_LINK') {
        state.menuInfoList[index]['recent_count'] = menuRecentCountInfo.mail;
      }
      if (menuInfo['menu_type'] === 'EA_LINK') {
        state.menuInfoList[index]['recent_count'] = menuRecentCountInfo.approval;
      }
      if (menuInfo['menu_type'] === 'SHARED_LINK') {
        state.menuInfoList[index]['recent_count'] = menuRecentCountInfo.shared;
      }
      if (menuInfo['menu_type'] === 'NOTES_LINK') {
        state.menuInfoList[index]['recent_count'] = menuRecentCountInfo.memo;
      }
    });
    state.menuInfoList = menuInfoList;
  },
  setMenuVisible(state, { menuType, isVisible }) {
    let foundIndex = state.menuInfoList.findIndex((menuInfo) => menuInfo.menu_type === menuType);
    state.menuInfoList[foundIndex]['show_more'] = isVisible ? 'Y' : 'N';
  },
  modifyMenuOrder(state, menuTypeList) {
    let newMenuList = [];

    menuTypeList.forEach((menuType, index) => {
      let menuIndex = state.menuInfoList.findIndex((menuInfo) => {
        return menuInfo.menu_type === menuType;
      });
      state.menuInfoList[menuIndex]['order'] = index;
      newMenuList.push(state.menuInfoList[menuIndex]);
    });

    state.menuInfoList = newMenuList;
  },
  setPlatformMenuAccess(state, access) {
    state.platformMenuAccess = access;
  },
};

const actions = {
  async loadMenuInfoList({ commit }) {
    return new Promise((resolve) => {
      commit('setMenuInfoList', []);
      menuApi
        .loadMenuInfoList()
        .then((apiResponse) => {
          let data = apiResponse.data;
          data.sort((a, b) => {
            return a.order - b.order;
          });
          commit('setMenuInfoList', data);
          commit('setDomain', data[0]['domain']);
        })
        .catch((error) => {
          if (error.response.status === 401) {
            alert('로그인이 필요합니다.');
            const officeId = common.getOfficeId();
            if (officeId) {
              window.document.location = `${process.env['VUE_APP_OFFICE_URL']}/${officeId}`;
            } else {
              window.document.location = 'https://hiworks.com/member/login';
            }
          }
        })
        .finally(() => {
          resolve();
        });
    });
  },
  async loadPlatformMenuInfoList({ commit }) {
    try {
      const result = await menuApi.loadPlatformMenuInfoList();

      commit('setPlatformMenuInfoList', result.data);

      return result.data;
    } catch (error) {
      console.error(error);
    }
  },
  async loadRecentCount({ commit }) {
    return new Promise((resolve) => {
      countApi
        .loadRecentMenuCount()
        .then((apiResponse) => {
          commit('setMenuRecentCount', apiResponse.data.data);
        })
        .finally(() => {
          resolve();
        });
    });
  },
  async setMenuVisible({ commit }, { menuType, isVisible }) {
    commit('setMenuVisible', { menuType, isVisible });
    return menuApi.setMenuVisible(menuType, isVisible);
  },
  async modifyMenuOrder({ commit }, menuTypeList) {
    return new Promise((resolve) => {
      menuApi
        .modifyMenuOrder(menuTypeList)
        .then(() => {
          commit('modifyMenuOrder', menuTypeList);
        })
        .finally(() => {
          resolve();
        });
    });
  },
  async loadPlatformAccessAuth({ commit }) {
    let result = {
      bill: false,
      accounting: false,
      hr: false,
      office: false,
      payroll: false,
      contract: false,
      asset: false,
    };

    try {
      const 경영사용권한 = await erpApi.getErpPermission();
      result['bill'] = 경영사용권한.data.data.tax_invoice.access_level_type !== 'UNUSED';
      result['accounting'] = 경영사용권한.data.data.accounting1.access_level_type !== 'UNUSED';
      result['common'] = 경영사용권한.data.data.common.access_level_type !== 'UNUSED';
    } catch (error) {
      console.error(error);
    }

    const [인사사용권한, 급여관리, 전자계약] = await Promise.allSettled([
      hrApi.getInsaManagerPermission(),
      hrApi.getPayrollManagerPermission(),
      hrApi.getContractManagerPermission(),
    ]);
    try {
      if (인사사용권한.status === 'fulfilled') {
        result['hr'] = 인사사용권한.value.data.data.is_insa_manager === 'Y';
        result['office'] = 인사사용권한.value.data.data.is_office_admin === 'Y';
      }

      if (급여관리.status === 'fulfilled') {
        result['payroll'] = 급여관리.value.data.data.is_payroll_manager;
      }

      if (전자계약.status === 'fulfilled') {
        result['contract'] = 전자계약.value.data.data.admin_flag === 'Y';
      }
    } catch (error) {
      console.error(error);
    }

    try {
      // 자산관리 API 요청
      const 자산관리권한 = await assetApi.getAssetPermission();
      result['asset'] = !!자산관리권한.data.data.length;
    } catch (error) {
      console.error(error);
    }

    commit('setPlatformMenuAccess', result);

    return result;
  },
};

export default {
  namespaced: true,
  state: initialState,
  mutations,
  actions,
  getters,
  strict: process.env.NODE_ENV !== 'production',
};
